import Vue from 'vue';
import axios from 'axios';
import VueAxios from 'vue-axios';
import NProgress from 'nprogress/nprogress';

import store from '@/store';
import JwtService from '@/common/jwt.service';
import { API_URL } from '@/common/config';

let isFetching = false;
let subscribers = [];

const ApiService = {
  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = API_URL;
    Vue.axios.defaults.headers = {
      Accept: 'application/json',
      'Accept-Language': 'nl-NL',
      'Content-Type': 'application/json'
    };
    this.setRequestInterceptors();
    this.setResponseInterceptors();
  },

  setRequestInterceptors() {
    Vue.axios.interceptors.request.use(
      config => {
        NProgress.start();
        config.headers.Authorization = `Bearer ${JwtService.getToken()}`;
        return config;
      },
      error => {
        NProgress.done();
        return Promise.reject(error.response);
      }
    );
  },

  setResponseInterceptors() {
    Vue.axios.interceptors.response.use(
      response => {
        NProgress.done();
        return response;
      },
      error => {
        const {
          config,
          response: { status }
        } = error;
        const original = config;

        if (status === 401 && store.state.auth.isAuthenticated && !isFetching) {
          isFetching = true;
          store.dispatch('auth/refresh').then(() => {
            isFetching = false;
            onTokenFetched(JwtService.getToken());
          });

          const retry = new Promise(resolve => {
            addSubscriber(token => {
              original.headers.Authorization = `Bearer ${token}`;
              resolve(Vue.axios(original));
            });
          });
          return retry;
        }

        NProgress.done();

        if (status === 500) {
          return Promise.reject({
            data: { message: 'Oops! Er is iets fout gegaan.' }
          });
        }

        return Promise.reject(error.response);
      }
    );
  },

  setAuthHeader() {
    Vue.axios.defaults.headers.common[
      'Authorization'
    ] = `Bearer ${JwtService.getToken()}`;
  },

  query(url, params) {
    return Vue.axios.get(url, params).catch(error => {
      throw new Error(`ApiService ${error}`);
    });
  },

  get(resource, slug = '') {
    const param = slug ? `/${slug}` : '';
    return Vue.axios.get(`${resource}${param}`).catch(error => {
      throw new Error(`ApiService ${error}`);
    });
  },

  post(resource, params, config = {}) {
    return Vue.axios.post(`${resource}`, params, config);
  },

  update(resource, slug, params) {
    return Vue.axios.put(`${resource}/${slug}`, params);
  },

  put(resource, params) {
    return Vue.axios.put(`${resource}`, params);
  },

  delete(resource) {
    return Vue.axios.delete(resource);
  }
};

export default ApiService;

function onTokenFetched(token) {
  subscribers = subscribers.filter(callback => callback(token));
}

function addSubscriber(callback) {
  subscribers.push(callback);
}
